'use client'

import React from 'react'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/ui/avatar'
import { Icons } from '@/components/ui/icons'
import { NotificationCenter } from '@/components/NotificationCenter'
import { useAI } from '@/context/AIContext'
import { useCommandPalette } from '@/context/CommandPaletteContext'
import { useSidebar } from './Sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// Breadcrumb helper with smart label mapping
function getBreadcrumbs(pathname: string): { label: string; href: string }[] {
  const segments = pathname.split('/').filter(Boolean)

  if (segments.length === 0) {
    return [{ label: 'Command Center', href: '/' }]
  }

  // Smart label mapping for common routes
  const labelMap: Record<string, string> = {
    tid: 'RFP',
    qa: 'Q&A',
    tids: 'RFPs',
    bids: 'Bids',
    evaluation: 'Evaluation',
    pcs: 'PCS and Award',
    vendor: 'Vendor',
    admin: 'Admin',
    knowledge: 'Knowledge Base',
    ai: 'AI',
    'e-auction': 'E-Auction',
    committee: 'E-Auction Committee',
    sourcing: 'PR Lifecycle',
    'pr-workspace': 'PR Workspace',
    'ai-workspace': 'PR Workspace',
    requests: 'My Requests',
    approvals: 'Approvals',
    workbench: 'Buyer Workspace',
    consolidation: 'Consolidation',
    exceptions: 'Auto Workbench',
    contracts: 'ARC Contracts',
    expiring: 'Expiring',
    dpr: 'DPR Module',
    'my-dpr': 'My DPR',
    spend: 'Spend Explorer',
    compliance: 'Contract & Compliance',
    savings: 'Savings Opportunities',
    avl: 'Approved Vendor List',
  }

  // Same wording as the tabs on those pages.
  const pathLabels: Record<string, string> = {
    '/avl/requests': 'Review requests',
    '/avl/exceptions': 'Exceptions',
  }

  const breadcrumbs = [{ label: 'Command Center', href: '/' }]

  let currentPath = ''
  segments.forEach((segment) => {
    currentPath += `/${segment}`
    
    // Skip numeric IDs and certain segments
    if (/^\d+$/.test(segment)) {
      return
    }
    
    // Use mapped label or capitalize
    const label = pathLabels[currentPath] || labelMap[segment.toLowerCase()] || 
      segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
    
    breadcrumbs.push({ label, href: currentPath })
  })

  return breadcrumbs
}

// Search input component
function SearchInput({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type='button'
      onClick={onOpen}
      className={cn(
        'group flex h-9 w-72 items-center gap-2 px-3',
        'rounded-md border border-border-strong bg-background-subtle',
        'text-sm text-text-secondary',
        'transition-colors',
        'hover:border-brand-navy/40 hover:bg-white',
        'focus:outline-none focus:ring-1 focus:ring-brand-navy'
      )}
    >
      <Icons.search
        className='h-4 w-4 transition-transform duration-200 group-hover:scale-110'
        strokeWidth={1.5}
      />
      <span className='flex-1 truncate text-left'>
        Search RFP, Vendors, Documents...
      </span>
      <kbd
        className={cn(
          'hidden h-5 items-center gap-1 rounded px-1.5 sm:inline-flex',
          'text-[10px] font-medium',
          'border border-border-default bg-background-subtle text-text-secondary',
          'transition-all duration-200',
          'group-hover:border-primary-500/40 group-hover:text-primary-600'
        )}
      >
        <Icons.command className='h-3 w-3' />K
      </kbd>
    </button>
  )
}

// AI Manager trigger button - styled as a prominent tag/badge
function AIManagerTrigger() {
  const { openManager, hasPendingSuggestions, isManagerOpen } = useAI()

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <button
            onClick={openManager}
            className={cn(
              'relative flex items-center gap-2 px-3 py-1.5',
              'rounded-md',
              'text-sm font-medium',
              'transition-colors',
              'bg-brand-navy',
              'text-white',
              'hover:bg-brand-navy-hover',
              isManagerOpen && 'ring-2 ring-brand-cornflower ring-offset-2 ring-offset-background',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower focus:ring-offset-2 focus:ring-offset-background'
            )}
            aria-label='Open AI Manager'
          >
            <Icons.sparkles
              className='h-4 w-4 text-white'
              strokeWidth={1.5}
            />
            <span className='hidden sm:inline'>AI Manager</span>
            <kbd className={cn(
              'hidden sm:inline-flex items-center gap-0.5',
              'px-1.5 py-0.5 rounded',
              'bg-white/20 text-white/90',
              'text-[10px] font-medium'
            )}>
              <Icons.command className='h-2.5 w-2.5' />J
            </kbd>
            {hasPendingSuggestions && (
              <span
                className={cn(
                  'absolute -right-1 -top-1',
                  'h-3 w-3 rounded-full',
                  'bg-brand-cornflower border-2 border-white',
                  'animate-pulse'
                )}
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side='bottom' className='sm:hidden'>
          <span>AI Manager</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// User menu with dropdown using Radix UI
function UserMenu() {
  const { data: session } = useSession()
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

  if (!session?.user) return null

  const handleLogout = () => {
    window.location.href = `${basePath}/api/auth/logout`
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'group flex items-center gap-1 rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-brand-cornflower focus:ring-offset-2 focus:ring-offset-background',
            'transition-transform duration-200'
          )}
        >
          <div className='flex items-center gap-3'>
            {/* User info - hidden on smaller screens */}
            <div className='hidden flex-col text-right lg:flex'>
              <span className='text-sm font-medium text-foreground'>
                {session.user.name}
              </span>
              <span className='text-xs text-text-secondary'>
                {session.user.email}
              </span>
            </div>

            {/* Avatar */}
            <Avatar
              src={session.user.image}
              fallback={session.user.name || session.user.email || '?'}
              size='md'
              showRing
            />
            <Icons.chevronDown
              className={cn(
                'h-4 w-4 text-text-secondary transition-transform duration-200',
                'group-data-[state=open]:rotate-180 group-hover:translate-y-0.5'
              )}
            />
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align='end' className='w-64'>
        {/* User info header */}
        <div className='border-b border-border-default px-3 py-3'>
          <div className='flex items-center gap-3'>
            <Avatar
              src={session.user.image}
              fallback={session.user.name || session.user.email || '?'}
              size='md'
            />
            <div className='min-w-0 flex-1'>
              <p className='truncate text-sm font-medium text-text-primary'>
                {session.user.name}
              </p>
              <p className='truncate text-xs text-text-secondary'>
                {session.user.email}
              </p>
            </div>
          </div>
        </div>

        {/* Menu items */}
        <div className='py-1'>
          <DropdownMenuItem className='gap-3 rounded-md px-3 py-2'>
            <Icons.user className='h-4 w-4 text-text-secondary' strokeWidth={1.5} />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuItem className='gap-3 rounded-md px-3 py-2'>
            <Icons.settings className='h-4 w-4 text-text-secondary' strokeWidth={1.5} />
            <span>Settings</span>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator />

        {/* Logout */}
        <div className='py-1'>
          <DropdownMenuItem
            onClick={handleLogout}
            destructive
            className='gap-3 rounded-md px-3 py-2'
          >
            <Icons.logout className='h-4 w-4' strokeWidth={1.5} />
            <span>Sign out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface HeaderProps {
  onOpenMobileMenu?: () => void
}

export function Header({ onOpenMobileMenu }: HeaderProps) {
  const pathname = usePathname()
  const breadcrumbs = getBreadcrumbs(pathname)
  const { open: openCommandPalette } = useCommandPalette()
  const { isCollapsed } = useSidebar()
  const isHome = pathname === '/'

  return (
    <header
      role='banner'
      className={cn(
        'fixed right-0 top-0 z-sticky',
        'left-0',
        isCollapsed ? 'md:left-[72px]' : 'md:left-[260px]',
        'bg-white',
        'border-b border-border-default',
        'flex items-center justify-between',
        'h-16 px-4 lg:px-6',
        'transition-[left] duration-300 ease-out'
      )}
    >
      {/* Left: Mobile menu */}
      <div className='flex min-w-0 items-center gap-2 md:flex-1'>
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={onOpenMobileMenu}
          className='-ml-1 shrink-0 text-brand-muted hover:text-foreground md:hidden'
          aria-label='Open navigation menu'
        >
          <Icons.menu className='h-5 w-5' strokeWidth={1.5} />
        </Button>

        {!isHome && (
          <nav
            aria-label='Breadcrumb'
            className='hidden min-w-0 items-center gap-1 text-sm md:flex'
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1
              
              return (
                <React.Fragment key={crumb.href}>
                  {index > 0 && (
                    <Icons.chevronRight
                      className='h-4 w-4 shrink-0 text-text-secondary/50'
                      aria-hidden='true'
                    />
                  )}
                  {isLast ? (
                    <span
                      className='truncate font-medium text-foreground'
                      aria-current='page'
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <button
                      onClick={() => window.location.href = crumb.href}
                      className={cn(
                        'truncate text-brand-muted',
                        'hover:text-foreground transition-colors',
                        'focus:outline-none focus:text-foreground'
                      )}
                    >
                      {crumb.label}
                    </button>
                  )}
                </React.Fragment>
              )
            })}
          </nav>
        )}
      </div>

      {/* Center: Search on command center */}
      <div className='hidden flex-1 justify-center px-4 lg:flex'>
        <SearchInput onOpen={openCommandPalette} />
      </div>

      {/* Right: Actions */}
      <div className='flex items-center gap-1 sm:gap-2'>
        <Button
          variant='ghost'
          size='icon-sm'
          className='text-brand-muted hover:text-foreground lg:hidden'
          aria-label='Search'
          onClick={openCommandPalette}
        >
          <Icons.search className='h-5 w-5' strokeWidth={1.5} />
        </Button>

        <Button
          variant='ghost'
          size='icon-sm'
          className='hidden text-brand-muted hover:text-foreground sm:inline-flex'
          aria-label='Help'
        >
          <Icons.helpCircle className='h-5 w-5' strokeWidth={1.5} />
        </Button>

        <AIManagerTrigger />

        <NotificationCenter />

        {/* Divider */}
        <div className='mx-1 hidden h-6 w-px bg-border/60 lg:block' />

        {/* User menu */}
        <UserMenu />
      </div>
    </header>
  )
}
